import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import isPlainObject from 'lodash/isPlainObject';
import uniqBy from 'lodash/uniqBy';

import { getContainerType } from '../shared/container.utils';
import { Container } from '../shared/models/container.model';
import { File } from '../shared/models/file.model';
import { ViewerApp, PropertyMatcher, MatchPredicate } from '../shared/models/site-settings.model';
import { getViewerType } from './types';

/**
 * 1. Try a parse in case the input is not truncated.
 * 2. Count { and [symbols and add their mirror symbols to a stack.
 * 3. Remove characters from truncated JSON until it is parseable.
 */
const openingSymbols = new Set(['{', '[']);
const closingSymbols = new Set(['}', ']']);
const mirrorSymbols = {
  '{': '}',
  '}': '{',
  '[': ']',
  ']': '[',
};
export function parseTruncatedJson(text: string): object {
  try {
    return JSON.parse(text);
  } catch (error) { } // eslint-disable-line no-empty

  const stack: string[] = [];
  let quoted = false;
  const characters = Array.from(text.trim());
  for (let index = 0; index < characters.length; index++) {
    if (characters[index - 1] === '\\') {
      continue;
    }
    const symbol = characters[index];
    if (symbol === '"') {
      quoted = !quoted;
      continue;
    }
    if (!quoted) {
      if (openingSymbols.has(symbol)) {
        stack.unshift(mirrorSymbols[symbol]);
      }
      if (closingSymbols.has(symbol)) {
        stack.shift();
      }
    }
  }
  if (quoted) {
    characters.push('"');
  }
  while (characters.length > 0) {
    try {
      return JSON.parse(characters.concat(stack).join(''));
    } catch (error) {
      characters.pop();
    }
  }
  throw new Error('Unparseable JSON');
}

export function parseTruncatedCsv(csv: string, delimiter = ','): {[key: string]: string}[] {
  if (csv.substr(-1) !== '\n' && csv.includes('\n')) {
    csv = csv.slice(0, csv.lastIndexOf('\n'));
  }
  // d3 expects an array of objects
  let rows: string[] = csv.split('\n');
  // remove blank rows
  rows = rows.filter(row => row.length);
  if (rows.length < 2) {
    return [];
  }
  const headers = rows.shift().split(delimiter).map(col => col.trim());
  return rows.map(rowString => {
    const rowSplit = rowString.split(delimiter);
    return headers.reduce((rowObj, column, i) => {
      rowObj[column] = rowSplit[i] && rowSplit[i] !== '' ? rowSplit[i].trim() : '';
      return rowObj;
    }, {});
  });
}

/**
 * Match a viewable container or file to a set of viewer apps.
 * If the file or container type matches, then check all field matchers
 * against the viewable's properties. If any matchers fail, then the app is
 * not matched; otherwise the app is associated to the viewable.
 * @param viewerApps Array of configured viewer apps
 * @param viewOptions File or container to test against application matchers
 * @return Applications that are associated to the container or file
 */
export function findAssociatedApps(viewerApps: ViewerApp[], viewOptions: { container?: Container, file?: File }): ViewerApp[] {
  const { container, file } = viewOptions;
  const fileType = file && file.type;
  const containerType = container && getContainerType(container);
  const apps = viewerApps.filter(app => {
    // check the file type and props
    if (fileType && app.files && fileType in app.files) {
      return matchProperties(file, app.files[fileType]);
    }
    // check container type and props
    if (containerType && app.containers && containerType in app.containers) {
      return matchProperties(container, app.containers[containerType]);
    }
    return false;
  });
  return uniqBy(apps, 'name');
}

export function hasBaseViewer(file: File): boolean {
  return file ? Boolean(getViewerType(file)) : false;
}

function matchProperties(item: Container | File, config: PropertyMatcher): boolean {
  // check all field matchers against item properties
  for (const [field, matcher] of Object.entries(config)) {
    const value = get(item, field);
    const predicate = getPredicate(matcher);
    if ('eq' in predicate && !isEqual(value, predicate.eq)) {
      return false;
    } else if ('exists' in predicate && (value !== undefined) !== predicate.exists) {
      return false;
    } else if ('regex' in predicate) {
      const regex = new RegExp(predicate.regex);
      const values = Array.isArray(value) ? value : [value];
      if (!values.some(item => regex.test(item))) {
        return false;
      }
    } else if ('contains' in predicate) {
      if (!Array.isArray(value) || !value.some(item => isEqual(item, predicate.contains))) {
        return false;
      }
    }
  }
  return true;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getPredicate(matcher: any): MatchPredicate {
  if (isPlainObject(matcher)) {
    return matcher;
  }
  if (typeof matcher === 'string' && (matcher.startsWith('^') || matcher.endsWith('$'))) {
    return { regex: matcher };
  }
  return { eq: matcher };
}
